import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiCheck,
  FiX,
  FiEye,
  FiRefreshCw,
  FiAlertCircle,
  FiSearch,
  FiClock
} from 'react-icons/fi';
import BaseUrl from '../../../Url/BaseUrl';
import { useTranslation } from 'react-i18next';

// Management Page Layout Component
const ManagementPage = ({ title, description, onBack, children }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="villagescity-management-page">
      <div className="villagescity-management-page__header">
        <button className="btn-back" onClick={() => navigate(-1)}>
          ← {t('back')}
        </button>
        <div className="villagescity-management-page__title-section">
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
      </div>
      <div className="villagescity-management-page__content">{children}</div>
    </div>
  );
};

// Village/City Approvals Component
const VillageCityApprovals = ({ onBack }) => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [processingApproval, setProcessingApproval] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  // Fetch pending approvals
  const fetchPendingApprovals = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BaseUrl.baseurl}CityVillages/GetPending`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setApprovals(data);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      setError('Failed to load pending approvals. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle approval action
  const handleApprovalAction = async (approvalItem, action) => {
    setProcessingApproval(approvalItem.cityVillageCode);
    setActionMessage(null);

    try {
      const updatedItem = {
        ...approvalItem,
        status: action === 'approve'
      };

      const response = await fetch(
        `${BaseUrl.baseurl}CityVillages/${approvalItem.cityVillageCode}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updatedItem)
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setApprovals((prev) =>
        prev.filter((item) => item.cityVillageCode !== approvalItem.cityVillageCode)
      );

      setActionMessage({
        type: 'success',
        text: `Successfully ${action === 'approve' ? 'approved' : 'rejected'} ${approvalItem.cityVillageName}`
      });

      setTimeout(() => setActionMessage(null), 3000);
    } catch (error) {
      console.error(`Error ${action}ing approval:`, error);
      setActionMessage({
        type: 'error',
        text: `Failed to ${action} ${approvalItem.cityVillageName}. Please try again.`
      });
    } finally {
      setProcessingApproval(null);
    }
  };

  // View details of an approval
  const viewApprovalDetails = (approvalItem) => {
    setSelectedApproval(approvalItem);
    setShowDetailsModal(true);
  };

  // Filter approvals based on search term
  const filteredApprovals = approvals.filter(
    (approval) =>
      approval.cityVillageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      approval.cityVillageCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      approval.districtCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Fetch data on component mount
  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  return (
    <>
      {/* Embedded CSS */}
      <style>
        {`
          /* Village/City Approvals - Enhanced UI Styles */
          .villagescity-management-page {
            max-width: 1400px;
            margin: 0 auto;
            padding: 2rem 1.5rem;
            font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
            background: #f8fafc;
            min-height: 100vh;
          }

          .villagescity-management-page__header {
            margin-bottom: 2rem;
          }

        

          .btn-back:hover {
            color: #2563eb;
            text-decoration: underline;
          }

          .villagescity-management-page__title-section h1 {
            font-size: 1.8rem;
            font-weight: 700;
            color: #0f172a;
            margin: 0.5rem 0 0.25rem;
          }

          .villagescity-management-page__title-section p {
            color: #475569;
            margin: 0;
            font-size: 0.95rem;
          }

          .villagescity-action-message {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.75rem 1rem;
            border-radius: 12px;
            margin-bottom: 1.5rem;
            font-size: 0.9rem;
            font-weight: 500;
            animation: slideDown 0.3s ease;
          }

          .villagescity-action-message--success {
            background: #dcfce7;
            color: #166534;
            border-left: 4px solid #22c55e;
          }

          .villagescity-action-message--error {
            background: #fee2e2;
            color: #991b1b;
            border-left: 4px solid #ef4444;
          }

          .villagescity-action-message__icon {
            font-size: 1.2rem;
          }

          .villagescity-approvals-controls {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            gap: 1rem;
            margin-bottom: 2rem;
            background: white;
            padding: 1rem 1.5rem;
            border-radius: 20px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.05);
            border: 1px solid #e2e8f0;
          }

          .villagescity-search-box {
            flex: 1;
            min-width: 240px;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            background: #f1f5f9;
            border-radius: 40px;
            padding: 0.5rem 1rem;
            transition: all 0.2s;
          }

          .villagescity-search-box:focus-within {
            background: white;
            box-shadow: 0 0 0 2px #3b82f6;
          }

          .villagescity-search-box__icon {
            color: #64748b;
            font-size: 1.1rem;
          }

          .villagescity-search-box__input {
            flex: 1;
            border: none;
            background: transparent;
            font-size: 0.9rem;
            outline: none;
            color: #0f172a;
          }

          .villagescity-search-box__input::placeholder {
            color: #94a3b8;
          }

          .villagescity-controls-right {
            display: flex;
            align-items: center;
            gap: 1rem;
          }

          .villagescity-btn-refresh {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            background: white;
            border: 1px solid #cbd5e1;
            padding: 0.5rem 1rem;
            border-radius: 40px;
            font-size: 0.85rem;
            font-weight: 500;
            color: #334155;
            cursor: pointer;
            transition: all 0.2s;
          }

          .villagescity-btn-refresh:hover:not(:disabled) {
            background: #f8fafc;
            border-color: #94a3b8;
          }

          .villagescity-btn-refresh:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .villagescity-spinning {
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          .villagescity-stats-badge {
            display: flex;
            align-items: center;
            gap: 0.4rem;
            background: #eef2ff;
            padding: 0.4rem 1rem;
            border-radius: 40px;
            font-size: 0.85rem;
            font-weight: 500;
            color: #1e40af;
          }

          .villagescity-stats-badge__count {
            font-weight: 700;
            font-size: 1rem;
            margin-left: 0.2rem;
          }

          .villagescity-approvals-container {
            background: transparent;
          }

          .villagescity-loading-state,
          .villagescity-error-state,
          .villagescity-empty-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 3rem 2rem;
            background: white;
            border-radius: 24px;
            border: 1px solid #e2e8f0;
            box-shadow: 0 1px 2px rgba(0,0,0,0.03);
          }

          .villagescity-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid #e2e8f0;
            border-top-color: #3b82f6;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
            margin-bottom: 1rem;
          }

          .villagescity-error-state__icon,
          .villagescity-empty-state__icon {
            font-size: 3rem;
            color: #cbd5e1;
            margin-bottom: 1rem;
          }

          .villagescity-btn-retry,
          .villagescity-btn-clear-search {
            margin-top: 1rem;
            background: #3b82f6;
            border: none;
            padding: 0.5rem 1.2rem;
            border-radius: 40px;
            color: white;
            font-weight: 500;
            cursor: pointer;
            transition: background 0.2s;
          }

          .villagescity-btn-retry:hover,
          .villagescity-btn-clear-search:hover {
            background: #2563eb;
          }

          .villagescity-approvals-list {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
            gap: 1.5rem;
          }

          .villagescity-approval-card {
            background: white;
            border-radius: 24px;
            border: 1px solid #e2e8f0;
            overflow: hidden;
            transition: all 0.25s ease;
            box-shadow: 0 2px 4px rgba(0,0,0,0.02);
          }

          .villagescity-approval-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 20px -12px rgba(0,0,0,0.1);
            border-color: #cbd5e1;
          }

          .villagescity-approval-card__header {
            padding: 1.25rem 1.5rem 0.75rem;
            border-bottom: 1px solid #f1f5f9;
            position: relative;
          }

          .villagescity-approval-card__badge {
            display: inline-block;
            background: #fee2e2;
            color: #b91c1c;
            font-size: 0.7rem;
            font-weight: 700;
            padding: 0.2rem 0.6rem;
            border-radius: 30px;
            margin-bottom: 0.75rem;
            letter-spacing: 0.5px;
          }

          .villagescity-approval-card__title {
            font-size: 1.25rem;
            font-weight: 700;
            margin: 0 0 0.25rem;
            color: #0f172a;
          }

          .villagescity-approval-card__code {
            font-size: 0.75rem;
            color: #64748b;
            font-family: monospace;
            background: #f1f5f9;
            display: inline-block;
            padding: 0.2rem 0.5rem;
            border-radius: 12px;
          }

          .villagescity-approval-card__content {
            padding: 1rem 1.5rem;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }

          .villagescity-approval-card__detail {
            display: flex;
            justify-content: space-between;
            font-size: 0.85rem;
            border-bottom: 1px dashed #f1f5f9;
            padding-bottom: 0.4rem;
          }

          .villagescity-approval-card__label {
            color: #475569;
            font-weight: 500;
          }

          .villagescity-approval-card__value {
            color: #0f172a;
            font-weight: 500;
          }

          .villagescity-approval-card__actions {
            padding: 1rem 1.5rem 1.5rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 1rem;
            flex-wrap: wrap;
            border-top: 1px solid #f1f5f9;
            background: #fefefe;
          }

          .villagescity-btn-action {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 1rem;
            border-radius: 40px;
            font-weight: 500;
            font-size: 0.85rem;
            cursor: pointer;
            transition: all 0.2s;
            border: none;
          }

          .villagescity-btn-view {
            background: #f1f5f9;
            color: #1e293b;
          }

          .villagescity-btn-view:hover:not(:disabled) {
            background: #e2e8f0;
          }

          .villagescity-approval-card__decision-actions {
            display: flex;
            gap: 0.6rem;
          }

          .villagescity-btn-reject {
            background: #fee2e2;
            color: #b91c1c;
          }

          .villagescity-btn-reject:hover:not(:disabled) {
            background: #fecaca;
          }

          .villagescity-btn-approve {
            background: #dcfce7;
            color: #15803d;
          }

          .villagescity-btn-approve:hover:not(:disabled) {
            background: #bbf7d0;
          }

          .villagescity-btn-action:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .villagescity-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.5);
            backdrop-filter: blur(4px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 1rem;
            animation: fadeIn 0.2s ease;
          }

          .villagescity-modal {
            background: white;
            max-width: 550px;
            width: 100%;
            border-radius: 32px;
            box-shadow: 0 25px 40px -12px rgba(0,0,0,0.3);
            overflow: hidden;
            animation: scaleUp 0.25s ease;
          }

          .villagescity-modal__header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.5rem 1.5rem 0.5rem;
            border-bottom: 1px solid #f1f5f9;
          }

          .villagescity-modal__header h2 {
            font-size: 1.5rem;
            font-weight: 700;
            margin: 0;
            color: #0f172a;
          }

          .villagescity-modal__close {
            background: none;
            border: none;
            font-size: 2rem;
            line-height: 1;
            cursor: pointer;
            color: #64748b;
            transition: color 0.2s;
          }

          .villagescity-modal__close:hover {
            color: #0f172a;
          }

          .villagescity-modal__content {
            padding: 1.5rem;
          }

          .villagescity-detail-section h3 {
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 1rem;
            color: #1e293b;
          }

          .villagescity-detail-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
            background: #f8fafc;
            padding: 1rem;
            border-radius: 20px;
            margin-bottom: 1.5rem;
          }

          .villagescity-detail-item {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }

          .villagescity-detail-item label {
            font-size: 0.7rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-weight: 600;
            color: #64748b;
          }

          .villagescity-detail-item span {
            font-weight: 500;
            color: #0f172a;
            word-break: break-word;
          }

          .villagescity-status-badge {
            display: inline-block;
            padding: 0.2rem 0.8rem;
            border-radius: 40px;
            font-size: 0.7rem;
            font-weight: 600;
            width: fit-content;
          }

          .villagescity-status-pending {
            background: #fef9c3;
            color: #854d0e;
          }

          .villagescity-modal__actions {
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
            margin-top: 1.5rem;
            padding-top: 1rem;
            border-top: 1px solid #f1f5f9;
          }

          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes scaleUp {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          @media (max-width: 768px) {
            .villagescity-management-page {
              padding: 1rem;
            }
            .villagescity-approvals-controls {
              flex-direction: column;
              align-items: stretch;
            }
            .villagescity-controls-right {
              justify-content: space-between;
            }
            .villagescity-approvals-list {
              grid-template-columns: 1fr;
            }
            .villagescity-approval-card__actions {
              flex-direction: column;
              align-items: stretch;
            }
            .villagescity-approval-card__decision-actions {
              justify-content: space-between;
            }
            .villagescity-detail-grid {
              grid-template-columns: 1fr;
            }
            .villagescity-modal {
              margin: 1rem;
              max-width: calc(100% - 2rem);
            }
          }
        `}
      </style>

      <ManagementPage
        title="Village/City Approval Management"
        description="Review and approve pending village/city submissions"
        onBack={onBack}
      >
        {/* Action Message */}
        {actionMessage && (
          <div
            className={`villagescity-action-message villagescity-action-message--${actionMessage.type}`}
          >
            <FiAlertCircle className="villagescity-action-message__icon" />
            <span>{actionMessage.text}</span>
          </div>
        )}

        {/* Controls Section */}
        <div className="villagescity-approvals-controls">
          <div className="villagescity-search-box">
            <FiSearch className="villagescity-search-box__icon" />
            <input
              type="text"
              placeholder="Search by name, code, or district..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="villagescity-search-box__input"
            />
          </div>

          <div className="villagescity-controls-right">
            <button
              className="villagescity-btn-refresh"
              onClick={fetchPendingApprovals}
              disabled={loading}
            >
              <FiRefreshCw className={loading ? 'villagescity-spinning' : ''} />
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>

            <div className="villagescity-stats-badge">
              <FiClock className="villagescity-stats-badge__icon" />
              <span className="villagescity-stats-badge__count">{approvals.length}</span>
              <span className="villagescity-stats-badge__text">Pending</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="villagescity-approvals-container">
          {loading ? (
            <div className="villagescity-loading-state">
              <div className="villagescity-spinner"></div>
              <p>Loading pending approvals...</p>
            </div>
          ) : error ? (
            <div className="villagescity-error-state">
              <FiAlertCircle className="villagescity-error-state__icon" />
              <p>{error}</p>
              <button className="villagescity-btn-retry" onClick={fetchPendingApprovals}>
                Try Again
              </button>
            </div>
          ) : filteredApprovals.length === 0 ? (
            <div className="villagescity-empty-state">
              <FiCheck className="villagescity-empty-state__icon" />
              <h3>No pending approvals</h3>
              <p>All village/city submissions have been processed.</p>
              {searchTerm && (
                <button
                  className="villagescity-btn-clear-search"
                  onClick={() => setSearchTerm('')}
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="villagescity-approvals-list">
              {filteredApprovals.map((approval) => (
                <div key={approval.cityVillageCode} className="villagescity-approval-card">
                  <div className="villagescity-approval-card__header">
                    <div className="villagescity-approval-card__badge">
                      <span className="villagescity-approval-card__badge-text">NEW</span>
                    </div>
                    <h3 className="villagescity-approval-card__title">{approval.cityVillageName}</h3>
                    <div className="villagescity-approval-card__code">{approval.cityVillageCode}</div>
                  </div>

                  <div className="villagescity-approval-card__content">
                    <div className="villagescity-approval-card__detail">
                      <span className="villagescity-approval-card__label">District Code:</span>
                      <span className="villagescity-approval-card__value">{approval.districtCode}</span>
                    </div>
                    <div className="villagescity-approval-card__detail">
                      <span className="villagescity-approval-card__label">District:</span>
                      <span className="villagescity-approval-card__value">
                        {approval.district || 'Not specified'}
                      </span>
                    </div>
                  </div>

                  <div className="villagescity-approval-card__actions">
                    <button
                      className="villagescity-btn-action villagescity-btn-view"
                      onClick={() => viewApprovalDetails(approval)}
                      disabled={processingApproval === approval.cityVillageCode}
                    >
                      <FiEye /> View Details
                    </button>

                    <div className="villagescity-approval-card__decision-actions">
                      <button
                        className="villagescity-btn-action villagescity-btn-reject"
                        onClick={() => handleApprovalAction(approval, 'reject')}
                        disabled={processingApproval === approval.cityVillageCode}
                      >
                        <FiX />
                        {processingApproval === approval.cityVillageCode ? 'Processing...' : 'Reject'}
                      </button>

                      <button
                        className="villagescity-btn-action villagescity-btn-approve"
                        onClick={() => handleApprovalAction(approval, 'approve')}
                        disabled={processingApproval === approval.cityVillageCode}
                      >
                        <FiCheck />
                        {processingApproval === approval.cityVillageCode ? 'Processing...' : 'Approve'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Details Modal */}
        {showDetailsModal && selectedApproval && (
          <div className="villagescity-modal-overlay">
            <div className="villagescity-modal">
              <div className="villagescity-modal__header">
                <h2>Approval Details</h2>
                <button
                  className="villagescity-modal__close"
                  onClick={() => setShowDetailsModal(false)}
                >
                  &times;
                </button>
              </div>

              <div className="villagescity-modal__content">
                <div className="villagescity-detail-section">
                  <h3>Village/City Information</h3>
                  <div className="villagescity-detail-grid">
                    <div className="villagescity-detail-item">
                      <label>Name:</label>
                      <span>{selectedApproval.cityVillageName}</span>
                    </div>
                    <div className="villagescity-detail-item">
                      <label>Code:</label>
                      <span>{selectedApproval.cityVillageCode}</span>
                    </div>
                    <div className="villagescity-detail-item">
                      <label>District Code:</label>
                      <span>{selectedApproval.districtCode}</span>
                    </div>
                    <div className="villagescity-detail-item">
                      <label>District:</label>
                      <span>{selectedApproval.district || 'Not specified'}</span>
                    </div>
                    <div className="villagescity-detail-item">
                      <label>Status:</label>
                      <span className="villagescity-status-badge villagescity-status-pending">
                        Pending Approval
                      </span>
                    </div>
                  </div>
                </div>

                <div className="villagescity-modal__actions">
                  <button
                    className="villagescity-btn-action villagescity-btn-reject"
                    onClick={() => {
                      handleApprovalAction(selectedApproval, 'reject');
                      setShowDetailsModal(false);
                    }}
                    disabled={processingApproval === selectedApproval.cityVillageCode}
                  >
                    <FiX /> Reject
                  </button>
                  <button
                    className="villagescity-btn-action villagescity-btn-approve"
                    onClick={() => {
                      handleApprovalAction(selectedApproval, 'approve');
                      setShowDetailsModal(false);
                    }}
                    disabled={processingApproval === selectedApproval.cityVillageCode}
                  >
                    <FiCheck /> Approve
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </ManagementPage>
    </>
  );
};

export default VillageCityApprovals;